"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TwilioWallSync = void 0;
const logger_1 = require("../util/logger");
const rxjs_1 = require("rxjs");
const twilio_sync_1 = require("twilio-sync");
const toolbox_1 = require("@streem/toolbox");
const minimal_1 = require("protobufjs/minimal");
const LOG_LEVEL = 'info';
class TwilioWallSync {
    // TODO wire up sync for keeping the local wall items on the remote wall
    // e.g. if I lose connection and add/remote items on the wall, when connection is established
    // add/remove any local items that are not on/off the remote
    constructor(wallItemClass, opts) {
        this.wallItemClass = wallItemClass;
        this.opts = opts;
        this.connectedSubject = new rxjs_1.BehaviorSubject(false);
        this.eventSubject = new rxjs_1.Subject();
        this.reconnectFailedSubject = new rxjs_1.BehaviorSubject(false);
        this.connectionState = 'unknown';
        this.reconnectInProgress = false;
        this.tokenExpiredSubject = new rxjs_1.BehaviorSubject(false);
        this.events = this.eventSubject.asObservable();
        this.connected = this.connectedSubject.asObservable();
        this.tokenExpired = this.tokenExpiredSubject.asObservable();
        this.reconnectFailed = this.reconnectFailedSubject.asObservable();
        this.wallSyncConnectionState = this.connectionState;
        this.handleRemoteSyncEvent = (type, event) => __awaiter(this, void 0, void 0, function* () {
            if (event.isLocal)
                return;
            const wallItem = yield this.getWallItemFromSyncEvent(event);
            if (!wallItem) {
                logger_1.logger.warn(`TwilioSyncClient: No WallItem found for eventType=${type} wallItemEvent=${event}`);
                return;
            }
            // don't spam logs on heartbeat.
            const level = wallItem.type === 'heartbeat' ? 'debug' : 'info';
            logger_1.logger[level](`TwilioSyncClient received wallItemType=${wallItem.type} eventType=${type} for wallItemId=${wallItem.id}`);
            this.eventSubject.next({
                type: type,
                itemId: wallItem.id,
                itemType: wallItem.type,
                item: wallItem,
            });
        });
        this.isSyncConnected = () => this.connectedSubject.value;
        this.isSyncClientConnected = () => this.connectionState === 'connected';
        this.getWallItemFromSyncEvent = (event) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const syncData = (_b = (_a = event === null || event === void 0 ? void 0 : event.item) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : event === null || event === void 0 ? void 0 : event.previousItemData;
            if (this.isTwilioWallItem(syncData)) {
                return this.decodeWallItem(syncData);
            }
        });
        this.decodeWallItem = (syncData) => __awaiter(this, void 0, void 0, function* () {
            var _c;
            const encodedWallItem = (_c = syncData.wall_item_encrypted) !== null && _c !== void 0 ? _c : syncData.wall_item;
            const encrypted = !!syncData.wall_item_encrypted;
            let buffer = minimal_1.util.newBuffer(minimal_1.util.base64.length(encodedWallItem));
            minimal_1.util.base64.decode(encodedWallItem, buffer, 0);
            if (encrypted) {
                const iv = buffer.slice(0, 12);
                const cipherText = buffer.slice(12, buffer.length);
                const decodedByteArray = yield crypto.subtle.decrypt({ name: 'AES-GCM', iv }, this.encryptionKey, cipherText);
                buffer = new Uint8Array(decodedByteArray);
            }
            return this.wallItemClass.decode(buffer);
        });
        this.encodeWallItem = (item) => __awaiter(this, void 0, void 0, function* () {
            const base64Buffer = this.wallItemClass.encode(item).finish();
            if (!this.encryptionKey) {
                return {
                    wall_item: minimal_1.util.base64.encode(base64Buffer, 0, base64Buffer.length),
                };
            }
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encryptedBuffer = new Uint8Array(yield crypto.subtle.encrypt({ name: 'AES-GCM', iv }, this.encryptionKey, base64Buffer));
            // Append the IV to the front of the byte array for decryption later
            const wallBuffer = new Uint8Array(iv.length + encryptedBuffer.byteLength);
            wallBuffer.set(iv, 0);
            wallBuffer.set(encryptedBuffer, iv.byteLength);
            return {
                wall_item_encrypted: minimal_1.util.base64.encode(wallBuffer, 0, wallBuffer.length),
            };
        });
        this.reconnectIfNotConnected = () => {
            logger_1.logger.info(`TwilioSync checking if connection still good. isSyncClientConnected=${this.isSyncClientConnected()}`);
            if (document.visibilityState === 'visible' && !this.isSyncClientConnected()) {
                logger_1.logger.warn(`Returned to Window and TwilioSyncClient connectionState=${this.connectionState}.  Reconnecting...`);
                this.reconnect();
            }
        };
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info(`TwilioSyncClient connecting connectionState=${this.connectionState}`);
            this.connectedSubject.next(false);
            const credentials = this.opts.initialCreds
                ? this.opts.initialCreds
                : yield this.opts.refreshToken();
            if (credentials.dataKey) {
                logger_1.logger.info(`TwilioSyncClient found dataKey in credentials. Proceeding using encrypted wall feature. connectionState=${this.connectionState}`);
                const dataKeyBuffer = minimal_1.util.newBuffer(minimal_1.util.base64.length(credentials.dataKey));
                minimal_1.util.base64.decode(credentials.dataKey, dataKeyBuffer, 0);
                this.encryptionKey = yield crypto.subtle.importKey('raw', dataKeyBuffer, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
            }
            logger_1.logger.info(`TwilioSyncClient connecting for wallMapName=${credentials.mapName}`);
            if (this.twilioSyncClient) {
                logger_1.logger.info(`TwilioSyncClient already exists; updating credentials token connectionState=${this.connectionState}`);
                yield this.twilioSyncClient.updateToken(credentials.token);
            }
            else {
                logger_1.logger.info(`TwilioSyncClient creating new SyncClient connectionState=${this.connectionState}`);
                this.twilioSyncClient = new twilio_sync_1.SyncClient(credentials.token, {
                    logLevel: process.env['NODE_ENV'] === 'production' ? 'info' : LOG_LEVEL,
                });
                this.twilioSyncClient
                    .on('connectionError', (error) => __awaiter(this, void 0, void 0, function* () {
                    logger_1.logger.error(`TwilioSyncClient Connection Error for wallMapName=${credentials.mapName} connectionState=${this.connectionState} message=${error.message} error=${JSON.stringify(error)}`);
                    // This only handles token expiration errors. Other errors are logged, but no action is taken.
                    if (error.terminal) {
                        // Note: there is a bug in the Twilio ConnectionError shape. It is missing the errorCode property even though the docs have it.
                        logger_1.logger.error(`TwilioSyncClient had a terminal error for wallMapName=${credentials.mapName} connectionState=${this.connectionState} errorCode=${error.errorCode}`);
                        // https://www.twilio.com/docs/api/errors/20104 - token expired
                        if (error.errorCode === 20104) {
                            logger_1.logger.warn(`TwilioSyncClient token has expired, attempting to refresh; wallMapName=${credentials.mapName} connectionState=${this.connectionState}`);
                            const data = yield this.opts.refreshToken();
                            yield this.twilioSyncClient.updateToken(data.token);
                            yield this.reconnect();
                        }
                        else if (error.errorCode === null) {
                            logger_1.logger.warn(`TwilioSyncClient has null errorCode, attempting reconnect...`);
                            yield this.reconnect();
                        }
                    }
                }))
                    .on('connectionStateChanged', (newState) => {
                    /**
                     * Client connection state.
                     * Directly reflects connection state of the underlying websocket transport.
                     * Possible values are as follows:
                     * * 'connecting' - client is offline and connection attempt is in process.
                     * * 'connected' - client is online and ready.
                     * * 'disconnecting' - client is going offline as disconnection is in process.
                     * * 'disconnected' - client is offline and no connection attempt is in process.
                     * * 'denied' - client connection is denied because of invalid JWT access token. User must refresh token in order to proceed.
                     * * 'error' - client connection is in a permanent erroneous state. Client re-initialization is required.
                     */
                    const previousState = this.connectionState;
                    this.connectionState = newState;
                    this.wallSyncConnectionState = newState;
                    logger_1.logger.info(`TwilioSyncClient Connection state changed for wallMapName=${credentials.mapName} from previousConnectionState=${previousState} to connectionState=${this.connectionState}`);
                })
                    // https://www.twilio.com/docs/sync/identity-and-access-tokens#handling-token-expiry
                    .on('tokenAboutToExpire', () => __awaiter(this, void 0, void 0, function* () {
                    logger_1.logger.info(`TwilioSyncClient token about to expire, attempting to refresh; wallMapName=${credentials.mapName} connectionState=${this.connectionState}`);
                    (0, toolbox_1.invariant)(this.twilioSyncClient, 'TwilioSyncClient.connect was not called');
                    try {
                        const data = yield this.opts.refreshToken();
                        yield this.twilioSyncClient.updateToken(data.token);
                    }
                    catch (err) {
                        logger_1.logger.error(`TwilioSyncClient failed to refresh token. wallMapName=${credentials.mapName} connectionState=${this.connectionState}`, err);
                    }
                }))
                    .on('tokenExpired', () => __awaiter(this, void 0, void 0, function* () {
                    logger_1.logger.info(`TwilioSyncClient token expired for wallMapName=${credentials.mapName} connectionState=${this.connectionState}. Attempting to reconnect...`);
                    // alerts observable listeners that the sync client has been destroyed
                    // after a new connection, remember to reestablish any syncClient event listeners (i.e. syncMap/wall events)
                    yield this.reconnect();
                }));
            }
            try {
                (0, toolbox_1.invariant)(this.twilioSyncClient, `TwilioSyncClient.connect was not called for wallMapName=${credentials.mapName} connectionState=${this.connectionState}`);
                this.syncMap = yield this.twilioSyncClient.map(credentials.mapName);
                logger_1.logger.info(`TwilioSyncClient initialized. wallMapName=${credentials.mapName} connectionState=${this.connectionState}`);
                yield this.loadExistingWallItemEvents();
                this.syncMap.on('itemAdded', evt => this.handleRemoteSyncEvent('ADDED', evt));
                this.syncMap.on('itemUpdated', evt => this.handleRemoteSyncEvent('UPDATED', evt));
                this.syncMap.on('itemRemoved', evt => this.handleRemoteSyncEvent('DELETED', evt));
                logger_1.logger.info(`TwilioSyncClient initialization completed. wallMapName=${credentials.mapName} connectionState=${this.connectionState}`);
                this.connectedSubject.next(true);
            }
            catch (error) {
                logger_1.logger.error(`TwilioSyncClient unable to connect. wallMapName=${credentials.mapName} connectionState=${this.connectionState}`, error);
            }
            document.addEventListener('visibilitychange', this.reconnectIfNotConnected, false);
        });
    }
    reconnect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.reconnectInProgress) {
                logger_1.logger.warn(`TwilioSyncClient reconnect already in progress... connectionState=${this.connectionState}`);
                return;
            }
            this.reconnectInProgress = true;
            logger_1.logger.info(`TwilioSyncClient attempting to reconnect. connectionState=${this.connectionState}`);
            if (this.twilioSyncClient) {
                logger_1.logger.info(`TwilioWallSync::reconnect(): TwilioSyncClient shutting down. connectionState=${this.connectionState}`);
                yield this.twilioSyncClient.shutdown();
                this.twilioSyncClient = undefined;
                (_a = this.syncMap) === null || _a === void 0 ? void 0 : _a.close();
            }
            const MAX_RETRIES = 3;
            const RETRY_DELAY = 2000; // Delay between retries in milliseconds to avoid concurrent reconnection tries
            for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                logger_1.logger.info(`TwilioSyncClient reconnect attempt ${attempt}`);
                try {
                    this.opts.initialCreds = null;
                    yield this.connect();
                    if (this.twilioSyncClient) {
                        this.reconnectInProgress = false;
                        this.tokenExpiredSubject.next(false);
                        logger_1.logger.info(`TwilioSyncClient reconnect success on attempt ${attempt}`);
                        return; // Reconnect successful, exit the retry loop
                    }
                }
                catch (e) {
                    logger_1.logger.error(`TwilioSyncClient failed reconnect attempt ${attempt}. exception=${e}`);
                }
                if (attempt < MAX_RETRIES) {
                    yield new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
                }
            }
            // Reconnect failed after maximum retries
            this.reconnectInProgress = false;
            this.tokenExpiredSubject.next(true);
            this.reconnectFailedSubject.next(true);
            logger_1.logger.error(`TwilioSyncClient reconnect failed after ${MAX_RETRIES} attempts`);
        });
    }
    disconnect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info(`TwilioSyncClient disconnecting. connectionState=${this.connectionState}`);
            if (!this.isSyncConnected()) {
                logger_1.logger.warn(`TwilioSyncClient failed to disconnect. connectionState=${this.connectionState}`);
                return;
            }
            logger_1.logger.info(`TwilioSyncClient closing syncMap. connectionState=${this.connectionState}`);
            (_a = this.syncMap) === null || _a === void 0 ? void 0 : _a.close();
            logger_1.logger.info(`TwilioSyncClient shutting down. connectionState=${this.connectionState}`);
            yield this.twilioSyncClient.shutdown();
            this.twilioSyncClient = undefined;
            this.eventSubject.complete();
            this.opts.onDisconnect();
            this.connectedSubject.next(false);
            this.syncMap = undefined;
            document.removeEventListener('visibilitychange', this.reconnectIfNotConnected, false);
        });
    }
    add(item) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.syncMap, 'TwilioWallSync.connect was not called');
            if (!this.isSyncClientConnected()) {
                logger_1.logger.warn(`TwilioSyncClient not in 'connected' connection state. Going to try reconnect then ADD WallItem wallItemType=${item.type} wallItemId=${item.id}`);
                yield this.reconnect();
            }
            if (this.isSyncClientConnected()) {
                const wallItem = yield this.encodeWallItem(item);
                try {
                    yield this.syncMap.set(item.id, wallItem);
                    logger_1.logger.info(`TwilioSyncClient ADDED WallItem wallItemType=${item.type} wallItemId=${item.id}`);
                }
                catch (e) {
                    logger_1.logger.error(`TwilioSyncClient failed to ADD wallItemType=${item.type} wallItemId=${item.id}, attempting to reconnect SyncClient.`, e);
                    if (this.connectionState == 'connected') {
                        // connectionState is not updated/correct, so change to 'disconnected' so it can reconnect.
                        this.connectionState = 'disconnected';
                        yield this.reconnect();
                    }
                }
            }
            else {
                logger_1.logger.error(`TwilioSyncClient not in 'connected' state and failed to ADD WallItem wallItemType=${item.type} wallItemId=${item.id}`);
            }
        });
    }
    update(itemId, item) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.syncMap, 'TwilioWallSync.connect was not called');
            if (!this.isSyncClientConnected()) {
                logger_1.logger.warn(`TwilioSyncClient not in 'connected' connection state. Going to try reconnect then UPDATE WallItem wallItemType=${item.type} wallItemId=${item.id}`);
                yield this.reconnect();
            }
            if (this.isSyncClientConnected()) {
                try {
                    const wallItem = yield this.encodeWallItem(item);
                    yield this.syncMap.update(itemId, wallItem);
                    // don't spam logs on heartbeat.
                    const level = item.type === 'heartbeat' ? 'debug' : 'info';
                    logger_1.logger[level](`TwilioSyncClient UPDATED WallItem wallItemType=${item.type} wallItemId=${item.id}`);
                }
                catch (e) {
                    logger_1.logger.error(`TwilioSyncClient failed to UPDATE wallItemType=${item.type} wallItemId=${item.id}, attempting to reconnect SyncClient.`, e);
                    if (this.connectionState == 'connected') {
                        // connectionState is not updated/correct, so change to 'disconnected' so it can reconnect.
                        this.connectionState = 'disconnected';
                        yield this.reconnect();
                    }
                }
            }
            else {
                logger_1.logger.error(`TwilioSyncClient not in 'connected' state, thus failed to UPDATE WallItem wallItemType=${item.type} wallItemId=${item.id}`);
            }
        });
    }
    delete(itemId) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.syncMap, 'TwilioWallSync.connect was not called');
            if (!this.isSyncClientConnected()) {
                logger_1.logger.warn(`TwilioSyncClient not in 'connected' connection state. Going to try reconnect then DELETE WallItem wallItemId=${itemId}`);
                yield this.reconnect();
            }
            if (this.isSyncClientConnected()) {
                try {
                    yield this.syncMap.remove(itemId);
                    logger_1.logger.info(`TwilioSyncClient DELETED wallItemId=${itemId}`);
                    return true;
                }
                catch (e) {
                    logger_1.logger.error(`TwilioSyncClient failed to delete item${itemId} from TwilioSyncMap, attempting to reconnect SyncClient.`, e);
                    if (this.connectionState == 'connected') {
                        // connectionState is not updated/correct, so change to 'disconnected' so it can reconnect.
                        this.connectionState = 'disconnected';
                        yield this.reconnect();
                    }
                    return false;
                }
            }
            else {
                logger_1.logger.error(`TwilioSyncClient in 'connected' connection state and failed to DELETE wallItemId=${itemId}`);
                return false;
            }
        });
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.syncMap, 'TwilioWallSync.connect was not called');
            if (!this.isSyncClientConnected()) {
                logger_1.logger.warn(`TwilioSyncClient not in 'connected' connection state. Going to try reconnect then CLEAR Wall`);
                yield this.reconnect();
            }
            if (this.isSyncClientConnected()) {
                try {
                    logger_1.logger.info(`TwilioSyncClient CLEARING wall and resetting connectionState=${this.connectionState}`);
                    yield this.syncMap.removeMap();
                }
                catch (e) {
                    logger_1.logger.error(`TwilioSyncClient failed to CLEAR wall, attempting to reconnect SyncClient.`, e);
                    if (this.connectionState == 'connected') {
                        // connectionState is not updated/correct, so change to 'disconnected' so it can reconnect.
                        this.connectionState = 'disconnected';
                        yield this.reconnect();
                    }
                }
            }
            else {
                logger_1.logger.error(`TwilioSyncClient not in 'connected' state, thus failed to CLEAR Wall`);
            }
        });
    }
    loadExistingWallItemEvents() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info(`TwilioSyncClient loading existing wall items connectionState=${this.connectionState}`);
            if (!this.syncMap) {
                logger_1.logger.warn(`TwilioSyncClient failed to attempt to load existing wall items. Wall not initialized connectionState=${this.connectionState}`);
                return;
            }
            let currentPaginator = yield this.syncMap.getItems();
            // Get Wall Items from Twilio and decode them.
            while (currentPaginator && currentPaginator.items) {
                const decodeProcesses = currentPaginator.items.map((item) => __awaiter(this, void 0, void 0, function* () {
                    if (!this.isTwilioWallItem(item.data)) {
                        logger_1.logger.warn('TwilioSyncClient encountered an invalid Twilio Wall Item', item.data);
                        return;
                    }
                    const wallItem = yield this.decodeWallItem(item.data);
                    const event = {
                        type: 'ADDED',
                        itemId: item.key,
                        itemType: wallItem.type,
                        item: wallItem,
                    };
                    this.eventSubject.next(event);
                }));
                // should the statuses of the promises be checked?
                yield Promise.allSettled(decodeProcesses);
                if (currentPaginator.hasNextPage) {
                    currentPaginator = yield currentPaginator.nextPage();
                }
                else {
                    break;
                }
            }
        });
    }
    isTwilioWallItem(data) {
        if (!data)
            return false;
        return typeof data.wall_item === 'string' || typeof data.wall_item_encrypted === 'string';
    }
}
exports.TwilioWallSync = TwilioWallSync;
//# sourceMappingURL=twilio_wall_sync.js.map